import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

type Status = 'public' | 'draft' | 'private' | 'archived' | 'unknown';

export interface ContentStatusPillProps {
  /** Content object — supports numeric status codes (proto) and string labels. */
  content: { status?: number | string };
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

// Numeric proto codes → string label. Matches getStatusLabel() from ContentCard.
const NUM_TO_LABEL: Record<number, Status> = {
  0: 'draft',
  1: 'draft',
  2: 'private',
  3: 'public',
  4: 'archived',
};

const resolveStatus = (raw: number | string | undefined): Status => {
  if (typeof raw === 'number') return NUM_TO_LABEL[raw] ?? 'unknown';
  if (typeof raw === 'string') {
    const lower = raw.toLowerCase();
    if (lower === 'public' || lower === 'draft' || lower === 'private' || lower === 'archived') {
      return lower;
    }
  }
  return 'unknown';
};

export const ContentStatusPill: React.FC<ContentStatusPillProps> = ({ content, sx }) => {
  const status = resolveStatus(content.status);
  return (
    <Box
      component="span"
      data-status={status}
      sx={[
        (theme) => {
          const styles: Record<Status, { bg: string; color: string }> = {
            public:   { bg: 'rgba(16,185,129,.12)',  color: theme.palette.mode === 'dark' ? '#34D399' : '#047857' },
            draft:    { bg: 'rgba(245,158,11,.14)',  color: theme.palette.mode === 'dark' ? '#FBBF24' : '#B45309' },
            private:  { bg: 'rgba(34,181,220,.14)',  color: theme.palette.brand.teal },
            archived: { bg: 'rgba(100,116,139,.14)', color: theme.palette.mode === 'dark' ? '#94A3B8' : '#475569' },
            unknown:  { bg: 'rgba(100,116,139,.10)', color: theme.palette.text.secondary },
          };
          const pill = styles[status];
          return {
            display: 'inline-block',
            fontSize: 11.5,
            fontWeight: 700,
            padding: '2px 9px',
            borderRadius: '999px',
            letterSpacing: '0.02em',
            textTransform: 'lowercase',
            background: pill.bg,
            color: pill.color,
            flexShrink: 0,
          };
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {status}
    </Box>
  );
};

export default ContentStatusPill;
